import { tspErrorMessage } from "../../hooks/useTspApiResource";
import { placeLabel } from "../../utils/tspPlace";
import { cityDetailSteps, hasCityDetails, locationsByStop } from "./addJourneyCityDetails";

export const STEP_STATUS = Object.freeze({ pending: "pending", saving: "saving", done: "done", failed: "failed", skipped: "skipped" });

// Saves Add Journey's per-city details once the journey exists, through the same
// endpoints the journey page uses (so every server rule, permission and
// notification applies). Stops are handled one after another, each in the order
// of cityDetailSteps: the visit first, then what belongs to it.
//
// Nothing is all-or-nothing across endpoints, so the outcome is reported per
// step, and calling this again with the previous `results` RESUMES: a step that
// is done is never sent twice, and a failed or skipped one is tried again.
//
// results: { [formStopId]: { label, cityVisitId, steps: [{ key, label, status, message }] } }
// `onProgress(results)` is called after every change, for the progress dialog.
export default async function saveJourneyCityDetails({ journey, stops, detailsByStop, range, api, uploadMedia, previous = {}, onProgress = () => {} }) {
  const locations = locationsByStop(stops, journey.route);
  const results = JSON.parse(JSON.stringify(previous));
  const report = () => onProgress(JSON.parse(JSON.stringify(results)));

  const pending = stops.filter((stop) => hasCityDetails(detailsByStop[stop.id]));
  pending.forEach((stop) => {
    const steps = cityDetailSteps(detailsByStop[stop.id], api, { uploadMedia });
    const before = results[stop.id] || { cityVisitId: null, steps: [] };
    results[stop.id] = {
      label: placeLabel(stop),
      cityVisitId: before.cityVisitId,
      steps: steps.map((step) => {
        const old = before.steps.find((candidate) => candidate.key === step.key);
        return old && old.status === STEP_STATUS.done ? old : { key: step.key, label: step.label, status: STEP_STATUS.pending, message: "" };
      }),
    };
  });
  report();

  for (const stop of pending) {
    const entry = results[stop.id];
    const locationId = locations.get(stop.id);
    const steps = cityDetailSteps(detailsByStop[stop.id], api, { uploadMedia });

    for (const step of steps) {
      const state = entry.steps.find((candidate) => candidate.key === step.key);
      if (state.status === STEP_STATUS.done) continue;
      if (!locationId) {
        Object.assign(state, { status: STEP_STATUS.failed, message: "This city was not found on the saved route." });
        report();
        continue;
      }
      if (step.needsVisit && !entry.cityVisitId) {
        Object.assign(state, { status: STEP_STATUS.skipped, message: "Not saved: the city's visit was not recorded." });
        report();
        continue;
      }

      Object.assign(state, { status: STEP_STATUS.saving, message: "" });
      report();
      try {
        const saved = await step.run({ journeyId: journey.id, locationId, cityVisitId: entry.cityVisitId, range, plannedDate: stop.plannedDate });
        if (step.key === "visit") entry.cityVisitId = saved?.id ?? null;
        Object.assign(state, { status: STEP_STATUS.done });
      } catch (error) {
        const details = error?.response?.data?.details;
        // The visit was recorded by an earlier attempt whose reply was lost: use it.
        if (step.key === "visit" && details?.code === "CITY_ALREADY_VISITED" && details.visit_id) {
          entry.cityVisitId = details.visit_id;
          Object.assign(state, { status: STEP_STATUS.done });
        } else {
          Object.assign(state, { status: STEP_STATUS.failed, message: tspErrorMessage(error, "Could not be saved.") });
        }
      }
      report();
    }
  }

  const allDone = Object.values(results).every((entry) => entry.steps.every((step) => step.status === STEP_STATUS.done));
  return { results, allDone };
}
